// How the first-experience tests fit together (owner, 2026-10-07: "I am good to run multiple of these tests at
// once. We just need to be intentional that the different variants gel together well in the experience").
// Every test is drawn on its own (firstExperienceTests.ts), so a learner can be on any combination of sides;
// these are the rules that keep every combination a sensible experience. Each is tested in
// firstExperienceGelRules.test.ts, and AGENTS.md ("First-experience tests") lists them.
//
// Pure, with no imports: the review's navigation code, which runs at app startup, reads it.

// ───────────────────────── Signing up before the first review ─────────────────────────

// T1 ended 2026-10-19 (its definition stays in firstExperienceTests.ts): its "save first" side is everyone's. A signed-out
// visitor signs up on the questions screen, right after its preview, before the first review's question 1 ("save
// questions and start answering"); a visitor who reaches the review signed out anyway (the browser's back button)
// finds question 1 under the sign-up sheet. So nobody answers a question of the first review without an account,
// and everything at its end (T2b's voice check, T2c's station, the streak day, the exam question, the upsell) is for
// an account. The other side's wall (sign up after three answers) is gone.

// ───────────────────────── The upsell after the first review ─────────────────────────

/**
 * The upsell after the first review in production: the paywall pop-up over "great work", always in its Elite first
 * format (the Elite offer, then Pro yearly on "no thanks"), on the checkpoint after the streak page and the exam
 * question, for every first review. Owner, 2026-10-21: "bring the elite first popup back straight". From 2026-10-09
 * to 2026-10-21 it was version A2 drawn in the page ("Simplified"), which hid Pro yearly; after the first review the
 * pop-up earned $1.72 per viewer in 24 hours and Simplified $0.64. T6 may show the exam pass in its place, and the
 * in-page designs (a, a2, a3, b, c) are previews in development builds only (firstUpsellSwitch.ts).
 */
export function getFirstUpsellVersionInProduction(): "today" {
  return "today";
}

// ───────────────────────── The first review's length ─────────────────────────

// T2 ended 2026-10-27 (its definition stays in firstExperienceTests.ts): every first review asks 5 questions. Against
// today's 7 and 10, more learners finished it (78.6% of those who started, vs 75.9% and 71.6%) and so reached "great
// work" and its upsell, with the same first-day answers (people on 5 kept answering after it), returns and purchases;
// 10 was the worst side on every count. A set short of questions still ends it earlier.
export const FIRST_REVIEW_QUESTION_COUNT = 5;

/**
 * The mix of a first review of this many questions. Today's 7 are 5 multiple choice and 2 true/false; every
 * length keeps that share of true/false (rounded): 5 → 4 + 1, 7 → 5 + 2, 10 → 7 + 3. A set short of one type
 * fills the review from the other (composeFirstReviewCards), so a review has its length whenever the set has
 * that many multiple choice and true/false questions.
 */
export function getFirstReviewQuestionTypeTargets(questionCount: number): {
  multipleChoiceTarget: number;
  trueOrFalseTarget: number;
} {
  const trueOrFalseTarget = Math.round((questionCount * 2) / 7);
  return { multipleChoiceTarget: questionCount - trueOrFalseTarget, trueOrFalseTarget };
}

// ───────────────────────── T2b: the voice check at the end of the first review ─────────────────────────

// The voice check comes after the first review's last question (owner, 2026-10-08, after walking the first
// experience: "The voice challenge should be after the 7 questions"). It opens on the press after the last
// answer, the press that otherwise opens the "great work" page; "great work" and its paywall follow when the
// check closes, whichever way it closes (finished, skipped, or a microphone that could not be used). It is a
// one-minute quiz on what the learner has just answered, so every question of the review is behind it.
//
// It replaces a mark on the bar at 50% (70% in a 5-question review), which opened the check in the middle of
// the review. The bar has no mark for it now: the check comes where the bar ends.
//
// Every learner signs up before the first review's question 1 (above), so every learner who reaches its end is
// signed in. A signed-out learner has no voice check all the same.
//
// A first review of 4 questions or fewer (a set short of questions) has no voice check, as before; the test's
// exposure ("the first review starts") counts the same learners (useFirstReviewTestExposures.ts).
export const FIRST_REVIEW_MINIMUM_QUESTION_COUNT_FOR_VOICE_CHECK = 5;

/** Whether this first review ends with the voice check, before its "great work" page. */
export function doesFirstReviewEndWithVoiceCheck({
  isSignedIn,
  isOnTheVoiceCheckSide,
  numberOfFirstReviewQuestions,
}: {
  isSignedIn: boolean;
  isOnTheVoiceCheckSide: boolean;
  numberOfFirstReviewQuestions: number;
}): boolean {
  return (
    isSignedIn &&
    isOnTheVoiceCheckSide &&
    Number.isFinite(numberOfFirstReviewQuestions) &&
    numberOfFirstReviewQuestions >= FIRST_REVIEW_MINIMUM_QUESTION_COUNT_FOR_VOICE_CHECK
  );
}

// ───────────────────────── T2c: the OSCE station instead of the voice check ─────────────────────────

// T2c (owner, 2026-10-15) lives inside T2b's "voice_check" side and is for health students only: the learners a
// clinical examination is for (`isHealthRelatedStudentType`), with clinical exam practice switched on for them.
// On its "osce_station" side the first set's station opens on the press that would open the voice check, on its
// door (the learner can enter or skip it), and "great work" and its paywall follow when it closes, as after the
// voice check. The station uses the learner's one free trial station like any other (owner, 2026-10-15).
//
// A station is only there when the first set matched an examination and the learner may sit one (the review
// page's `useClinicalExamStationsDueInReview` works that out). Without one, the voice check opens as on the other
// side (owner, 2026-10-15: "fall back to voice check").

/** Whether this learner takes part in T2c: the voice check would end their first review, and they are a health
 * student the station is open to. Also the moment its exposure is reported. */
export function isInTheFirstReviewStationTest({
  endsWithVoiceCheck,
  isHealthStudent,
  isClinicalExamPracticeEnabled,
}: {
  endsWithVoiceCheck: boolean;
  isHealthStudent: boolean;
  isClinicalExamPracticeEnabled: boolean;
}): boolean {
  return endsWithVoiceCheck && isHealthStudent && isClinicalExamPracticeEnabled;
}

/** Whether the first review ends with the first set's station instead of the voice check. */
export function doesFirstReviewEndWithClinicalExamStation({
  isInTheTest,
  isOnTheStationSide,
  hasStationDueForTheFirstSet,
}: {
  isInTheTest: boolean;
  isOnTheStationSide: boolean;
  hasStationDueForTheFirstSet: boolean;
}): boolean {
  return isInTheTest && isOnTheStationSide && hasStationDueForTheFirstSet;
}

// ───────────────────────── The pages after the first review's last answer ─────────────────────────

// Every first review, at any length, ends with today's streak day (owner, 2026-10-09: "Have people achieve their
// day 1 streak after they finish the mini review (no matter what size the mini review is)"), so its streak page
// comes first, after T2b's voice check where there is one. The first upsell is drawn on the checkpoint, so the exam
// question comes before it (owner, 2026-10-09: "1. Mini first review 2. streak page 3. When's the exam? 4. Upsell 5.
// Checkpoint."): the checkpoint (with its upsell) closes the sequence, and its "continue" goes on to the next
// question. Since 2026-10-19 (T1 ended) this is every learner's order, a signed-in learner's first set included; the
// order it replaced asked the exam question after the checkpoint's "continue", behind the paywall modal. The exam
// question is for a student or a teacher only, as it always was.
export type PageAfterTheFirstReview = "voice_check" | "streak" | "exam_date" | "checkpoint";

/** The pages after the first review's last answer, in order */
export function getPagesAfterTheFirstReview({
  endsWithVoiceCheck,
  asksForTheExamDate,
}: {
  endsWithVoiceCheck: boolean;
  asksForTheExamDate: boolean;
}): PageAfterTheFirstReview[] {
  const pages: PageAfterTheFirstReview[] = [];
  if (endsWithVoiceCheck) pages.push("voice_check");
  pages.push("streak");
  if (asksForTheExamDate) pages.push("exam_date");
  pages.push("checkpoint");
  return pages;
}

// ───────────────────────── T5 and T5b: the free-generation page and the allowance ─────────────────────────

/**
 * T5's population: a signed-in account that has never made a set, on an allowance of one free generation a
 * month (the one-generation countries; T5b's "one_generation" side), meeting the free-generation page with that
 * generation still to use. Its headline, "this is your free generation for this month", is then exactly true.
 * Everyone else sees today's page: with none left there is nothing to make, and an account on two free
 * generations with one left has already used one. So T5b's "two_generations" accounts are never in T5, and
 * T5's readout is T5b's "one_generation" side plus the one-generation accounts made before T5b.
 */
export function isAccountEligibleForFirstSetLimitPage({
  userId,
  numberOfSetsGenerated,
  generationsLeft,
  monthlyGenerationsAllowed,
}: {
  userId: string | null | undefined;
  numberOfSetsGenerated: number | null | undefined;
  generationsLeft: number | null | undefined;
  monthlyGenerationsAllowed: number | null | undefined;
}): boolean {
  return (
    Boolean(userId) && !((numberOfSetsGenerated ?? 0) > 0) && monthlyGenerationsAllowed === 1 && generationsLeft === 1
  );
}

// ───────────────────────── T6: the exam pass after the first review ─────────────────────────

/** The exam pass's three lengths (owner, 2026-10-18). Each has its own Stripe price, so the tier fixes both. */
export type ExamPassTier = "short" | "two_weeks" | "month";

/** Days after the purchase day the pass runs to, through the end of that local day (the Worker's same rule). */
export const EXAM_PASS_EXTRA_DAYS_BY_TIER: Record<ExamPassTier, number> = { short: 2, two_weeks: 14, month: 30 };

/** From this many days before the exam the year ticket is chosen first; closer, the pass is. */
export const DAYS_BEFORE_THE_EXAM_FROM_WHICH_THE_YEAR_IS_CHOSEN_FIRST = 4;

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Calendar days from today to the exam's day, in the learner's own time zone: 0 today, 1 tomorrow. */
export function getCalendarDaysUntilExam(dateOfExam: Date, now: Date): number {
  return Math.round((startOfLocalDay(dateOfExam).getTime() - startOfLocalDay(now).getTime()) / 86_400_000);
}

/** Exam today or tomorrow: short; in 2–7 days: two weeks; in 8–30 days: a month; otherwise no pass. */
export function getExamPassTierForExam(dateOfExam: Date | null | undefined, now: Date): ExamPassTier | null {
  if (!(dateOfExam instanceof Date) || Number.isNaN(dateOfExam.getTime())) return null;
  const daysUntilExam = getCalendarDaysUntilExam(dateOfExam, now);
  if (daysUntilExam < 0 || daysUntilExam > 30) return null;
  if (daysUntilExam <= 1) return "short";
  if (daysUntilExam <= 7) return "two_weeks";
  return "month";
}

/** The last moment of the pass: the end of the local day `extraDays` after today, as the paywall prints it. */
export function getExamPassEndDate(tier: ExamPassTier, now: Date): Date {
  return new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + EXAM_PASS_EXTRA_DAYS_BY_TIER[tier],
    23,
    59,
    59,
    999
  );
}

/**
 * T6's population: the learners who would get production's pop-up after their first review (every first review; the
 * checkpoint keeps the test to a signed-in Free account), on the web (the pass is a Stripe payment; the apps sell
 * through their stores), whose exam date saved on the exam question before the checkpoint is 0–30 days away. Everyone
 * else keeps the upsell they would have had, so the test compares the two upsells on the same learners. Until
 * 2026-10-21 the control was A2 ("Simplified"); the side keeps its name `a2_upsell`, and the readout counts from the
 * switch.
 */
export function isInTheExamPassUpsellTest({
  firstUpsellVersion,
  isRunningAsMobileApp,
  examPassTier,
}: {
  firstUpsellVersion: string;
  isRunningAsMobileApp: boolean;
  examPassTier: ExamPassTier | null;
}): boolean {
  return firstUpsellVersion === "today" && !isRunningAsMobileApp && examPassTier !== null;
}
